import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Alert,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Pagination,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Skeleton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import EmailIcon from "@mui/icons-material/Email";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PeopleIcon from "@mui/icons-material/People";
import RefreshIcon from "@mui/icons-material/Refresh";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { ROUTE_BASE, ROUTE_CREATE_SURVEY, ROUTE_SURVEY, ROUTE_PREVIEW_SURVEY, ROUTE_EMAIL_SENDER } from "constants/routerPath";
import { useSurveys, useSurveyMutations } from "hooks/survey";
import useNotificationProvider from "hooks/notificationBar/useNotificationProvider";
import { downloadSurveyResultsExcel, getSurveyResults, deleteSurveyResult } from "api/cansurvey/surveyApi";
import type { SurveySummary, SurveyResultsResponse } from "types/survey";
import { useMediaQuery } from "@mui/material";
import ManageEditorsDialog from "./ManageEditorsDialog";
import AiSummaryDialog from "./AiSummaryDialog";
import NotificationsDialog from "./NotificationsDialog";

interface SurveyTableRowProps {
  survey: SurveySummary;
  responseCount: number | null;
  onCopyLink: (surveyId: number) => void;
  onView: (surveyId: number) => void;
  onPreview: (surveyId: number) => void;
  onEdit: (surveyId: number) => void;
  onDelete: (surveyId: number) => void;
  onDownload: (surveyId: number) => void;
  onSendViaCANmail: (surveyId: number) => void;
  onManageEditors: (surveyId: number) => void;
  onNotifications: (surveyId: number) => void;
  onDeleteResult: (surveyResultId: number) => void;
  onAiSummary: (surveyId: number) => void;
  isMobile: boolean;
  isDeleting: boolean;
  isDownloading: boolean;
  isExpanded: boolean;
  onToggle: (surveyId: number) => void;
  resultsRefreshKey: number;
}

const SurveyTableRow: React.FC<SurveyTableRowProps> = ({
  survey,
  responseCount,
  onCopyLink,
  onView,
  onPreview,
  onEdit,
  onDelete,
  onDownload,
  onSendViaCANmail,
  onManageEditors,
  onNotifications,
  onDeleteResult,
  onAiSummary,
  isMobile,
  isDeleting,
  isDownloading,
  isExpanded,
  onToggle,
  resultsRefreshKey,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const [resultsData, setResultsData] = useState<SurveyResultsResponse | null>(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsError, setResultsError] = useState<string | null>(null);
  const fetchingRef = useRef(false);

  useEffect(() => {
    if (isExpanded && resultsData === null && !fetchingRef.current) {
      fetchingRef.current = true;
      setResultsLoading(true);
      setResultsError(null);

      // Defer fetch to next tick so Collapse animation isn't blocked
      const timeout = setTimeout(() => {
        getSurveyResults(survey.SurveyId)
          .then((data) => {
            setResultsData(data);
          })
          .catch((err) => {
            console.error('[SurveyTableRow] Failed to load results:', err);
            setResultsError(err instanceof Error ? err.message : "Failed to load results");
          })
          .finally(() => {
            setResultsLoading(false);
            fetchingRef.current = false;
          });
      }, 0);

      return () => clearTimeout(timeout);
    }
  }, [isExpanded, survey.SurveyId, resultsData]);

  // Re-fetch results when a result is deleted
  useEffect(() => {
    if (resultsRefreshKey > 0 && isExpanded) {
      setResultsData(null);
      fetchingRef.current = false;
    }
  }, [resultsRefreshKey, isExpanded]);

  const handleMenuClose = () => setAnchorEl(null);

  const handleRefreshResults = () => {
    setResultsData(null);
    setResultsError(null);
    fetchingRef.current = false;
  };

  const renderExpandedContent = () => {
    if (resultsLoading) {
      return (
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" animation="wave" height={isMobile ? 80 : 40} />
          ))}
        </Box>
      );
    }

    if (resultsError) {
      return (
        <Alert severity="error" sx={{ my: 1 }}>
          {resultsError}
        </Alert>
      );
    }

    if (!resultsData || resultsData.results.length === 0) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 2 }}>
          <Typography variant="body1" color="text.secondary">
            No results yet. Share the survey to start collecting responses.
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ContentCopyIcon />}
            onClick={() => onCopyLink(survey.SurveyId)}
          >
            Copy Survey Link
          </Button>
        </Box>
      );
    }

    return (
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, px: 2, pt: 1 }}>
          <Chip
            label={`${resultsData.results.length} response${resultsData.results.length !== 1 ? "s" : ""}`}
            size="small"
            color="primary"
            variant="outlined"
          />
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="Refresh results">
              <IconButton size="small" onClick={handleRefreshResults}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="AI Summary">
              <IconButton size="small" onClick={() => onAiSummary(survey.SurveyId)}>
                <AutoAwesomeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download as Excel">
              <IconButton size="small" onClick={() => onDownload(survey.SurveyId)}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Mobile: card-based results */}
        {isMobile ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, px: 2, pb: 2 }}>
            {resultsData.results.map((result) => (
              <Box
                key={result.surveyResultId}
                sx={{ borderBottom: 1, borderColor: "divider", pb: 1.5, mb: 0.5 }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {result.name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => onDeleteResult(result.surveyResultId)}
                    sx={{ opacity: 0.4, "&:hover": { opacity: 1, color: "error.main" } }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  {format(new Date(result.completeDate), "MMM d, yyyy")}
                  {result.source ? ` \u2022 ${result.source}` : ""}
                </Typography>
                {resultsData.questions.map((question) => (
                  <Box key={question} sx={{ mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      {question}
                    </Typography>
                    <Typography variant="body2">
                      {result.answers[question] || "\u2014"}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        ) : (
          /* Desktop: table-based results */
          <TableContainer>
            <Table size="small" sx={{ tableLayout: "auto" }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.50" }}>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>Respondent</TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>Submitted</TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>Source</TableCell>
                  {resultsData.questions.map((question) => (
                    <TableCell key={question} sx={{ fontWeight: 600 }}>{question}</TableCell>
                  ))}
                  <TableCell sx={{ width: 48 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {resultsData.results.map((result) => (
                  <TableRow key={result.surveyResultId} hover>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{result.name}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {format(new Date(result.completeDate), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{result.source ?? "\u2014"}</TableCell>
                    {resultsData.questions.map((question) => (
                      <TableCell key={question}>{result.answers[question] || ""}</TableCell>
                    ))}
                    <TableCell sx={{ width: 48, p: 0.5 }}>
                      <Tooltip title="Delete response">
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); onDeleteResult(result.surveyResultId); }}
                          sx={{
                            opacity: 0.4,
                            "&:hover": { opacity: 1, color: "error.main" },
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  };

  // Shared menu component used by both mobile and desktop
  const renderMenu = () => (
    <Menu
      anchorEl={anchorEl}
      open={menuOpen}
      onClose={handleMenuClose}
      onClick={(e) => e.stopPropagation()}
    >
      <MenuItem
        onClick={() => { onView(survey.SurveyId); handleMenuClose(); }}
      >
        <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Take Survey</ListItemText>
      </MenuItem>
      <MenuItem
        onClick={() => { onPreview(survey.SurveyId); handleMenuClose(); }}
      >
        <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Preview</ListItemText>
      </MenuItem>
      <MenuItem
        onClick={() => { onDownload(survey.SurveyId); handleMenuClose(); }}
        disabled={isDownloading}
      >
        <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Download Results</ListItemText>
      </MenuItem>
      <MenuItem
        onClick={() => { onSendViaCANmail(survey.SurveyId); handleMenuClose(); }}
      >
        <ListItemIcon><EmailIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Send via CANmail</ListItemText>
      </MenuItem>
      <MenuItem
        onClick={() => { onNotifications(survey.SurveyId); handleMenuClose(); }}
      >
        <ListItemIcon><NotificationsIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Notifications</ListItemText>
      </MenuItem>
      {survey.IsCreator && (
        <MenuItem
          onClick={() => { onManageEditors(survey.SurveyId); handleMenuClose(); }}
        >
          <ListItemIcon><PeopleIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Manage Editors</ListItemText>
        </MenuItem>
      )}
      {survey.IsCreator && (
        <MenuItem
          onClick={() => { onDelete(survey.SurveyId); handleMenuClose(); }}
          disabled={isDeleting}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon><DeleteForeverIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      )}
    </Menu>
  );

  // Mobile card layout
  if (isMobile) {
    return (
      <Paper elevation={0} sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}>
        {/* Card header */}
        <Box
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", p: 2, pb: 1, cursor: "pointer" }}
          onClick={() => onToggle(survey.SurveyId)}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ wordBreak: "break-word" }}>
              {survey.SurveyTitle}
            </Typography>
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
              {!survey.IsCreator && (
                <Chip label="Editor" size="small" variant="outlined" color="info" />
              )}
              {survey.IsCreator && survey.EditorCount > 0 && (
                <Chip label="Shared" size="small" variant="outlined" color="success" />
              )}
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 1 }}>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggle(survey.SurveyId); }}>
              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); }}>
              <MoreVertIcon />
            </IconButton>
            {renderMenu()}
          </Box>
        </Box>

        {/* Card meta */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, pb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {format(new Date(survey.CreateDate), "MMM dd, yyyy")}
          </Typography>
          {responseCount === null ? (
            <CircularProgress size={16} />
          ) : (
            <Chip label={`${responseCount} response${responseCount !== 1 ? "s" : ""}`} size="small" variant="outlined" />
          )}
        </Box>

        {/* Card actions */}
        <Box sx={{ display: "flex", gap: 1, px: 2, pb: 2 }}>
          <Tooltip title="Copy link">
            <IconButton size="small" onClick={() => onCopyLink(survey.SurveyId)}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={responseCount === 0 || responseCount === null ? "No responses to summarize" : "AI Summary"}>
            <span>
              <IconButton
                size="small"
                onClick={() => onAiSummary(survey.SurveyId)}
                disabled={responseCount === 0 || responseCount === null}
              >
                <AutoAwesomeIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Edit survey">
            <IconButton size="small" onClick={() => onEdit(survey.SurveyId)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Expanded results */}
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box sx={{ borderTop: 1, borderColor: "divider" }}>
            {renderExpandedContent()}
          </Box>
        </Collapse>
      </Paper>
    );
  }

  // Desktop table row layout
  return (
    <React.Fragment>
      {/* Main Data Row */}
      <TableRow hover sx={{ cursor: "pointer" }} onClick={() => onToggle(survey.SurveyId)}>
        <TableCell sx={{ width: 50 }}>
          <IconButton
            aria-label={isExpanded ? "Collapse" : "Expand"}
            aria-expanded={isExpanded}
            size="small"
            onClick={(e) => { e.stopPropagation(); onToggle(survey.SurveyId); }}
          >
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          {survey.SurveyTitle}
          {!survey.IsCreator && (
            <Tooltip title="You were added as an editor on this survey. You can edit it and view results, but only the creator can delete it or manage editors.">
              <Chip label="Editor" size="small" variant="outlined" color="info" sx={{ ml: 1 }} />
            </Tooltip>
          )}
          {survey.IsCreator && survey.EditorCount > 0 && (
            <Tooltip title={`This survey is shared with ${survey.EditorCount} editor(s). Open Manage Editors from the actions menu to change.`}>
              <Chip label="Shared" size="small" variant="outlined" color="success" sx={{ ml: 1 }} />
            </Tooltip>
          )}
        </TableCell>
        <TableCell>
          {format(new Date(survey.CreateDate), "MMMM dd, yyyy HH:mm")}
        </TableCell>
        <TableCell align="center" sx={{ width: 90, px: 1 }}>
          {responseCount === null ? (
            <CircularProgress size={24} />
          ) : (
            <Chip label={`${responseCount}`} variant="outlined" sx={{ textAlign: "center" }} />
          )}
        </TableCell>
        <TableCell align="center" sx={{ width: 110, px: 1, whiteSpace: "nowrap" }}>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<ContentCopyIcon />}
            onClick={(e) => { e.stopPropagation(); onCopyLink(survey.SurveyId); }}
          >
            Copy
          </Button>
        </TableCell>
        <TableCell align="center" sx={{ width: 140, px: 1, whiteSpace: "nowrap" }}>
          <Tooltip title={responseCount === 0 || responseCount === null ? "No responses to summarize" : ""}>
            <span>
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                startIcon={<AutoAwesomeIcon />}
                onClick={(e) => { e.stopPropagation(); onAiSummary(survey.SurveyId); }}
                disabled={responseCount === 0 || responseCount === null}
              >
                AI Summary
              </Button>
            </span>
          </Tooltip>
        </TableCell>
        <TableCell align="center" sx={{ width: 100, px: 1, whiteSpace: "nowrap" }}>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            startIcon={<EditIcon />}
            onClick={(e) => { e.stopPropagation(); onEdit(survey.SurveyId); }}
          >
            Edit
          </Button>
        </TableCell>
        <TableCell align="center" sx={{ width: 80, px: 1 }}>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); }}
          >
            <MoreVertIcon />
          </IconButton>
          {renderMenu()}
        </TableCell>
      </TableRow>

      {/* Expanded Details Row */}
      <TableRow>
        <TableCell sx={{ p: 0, borderBottom: isExpanded ? 1 : 0, bgcolor: "white" }} colSpan={8}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ bgcolor: "white", overflowX: "auto" }}>
              {renderExpandedContent()}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

function SurveyHome() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [pageSize] = useState(25);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSurveyId, setSelectedSurveyId] = useState<number | null>(null);
  const [deletingSurveyId, setDeletingSurveyId] = useState<number | null>(null);
  const [downloadingSurveyId, setDownloadingSurveyId] = useState<number | null>(null);
  const [expandedSurveyId, setExpandedSurveyId] = useState<number | null>(null);
  const [editorsDialogSurveyId, setEditorsDialogSurveyId] = useState<number | null>(null);
  const [deleteResultDialogOpen, setDeleteResultDialogOpen] = useState(false);
  const [selectedResultId, setSelectedResultId] = useState<number | null>(null);
  const [deletingResult, setDeletingResult] = useState(false);
  const [resultsRefreshKey, setResultsRefreshKey] = useState(0);
  const [aiSummarySurveyId, setAiSummarySurveyId] = useState<number | null>(null);
  const [notificationsDialogSurveyId, setNotificationsDialogSurveyId] = useState<number | null>(null);

  const handleToggleExpand = (surveyId: number) => {
    setExpandedSurveyId((prev) => (prev === surveyId ? null : surveyId));
  };

  const isMobile = useMediaQuery('(max-width: 1000px)');
  const { surveys, pagination, responses, loading, error, refetch } = useSurveys(page, pageSize);
  const { deleteSurveyMutation, deleting: isDeleting } = useSurveyMutations();
  const { notifySuccess, notifyError } = useNotificationProvider();

  const handleCreateSurvey = () => {
    navigate(ROUTE_CREATE_SURVEY);
  };

  const handleCopyLink = async (surveyId: number) => {
    const survey = surveys.find((s) => s.SurveyId === surveyId);
    const surveyLink = survey?.PublicToken
      ? `${window.location.origin}${ROUTE_BASE}${ROUTE_SURVEY}?token=${survey.PublicToken}`
      : `${window.location.origin}${ROUTE_BASE}${ROUTE_SURVEY}?surveyId=${surveyId}`;
    try {
      await navigator.clipboard.writeText(surveyLink);
      notifySuccess("Survey link copied to clipboard!");
    } catch (error) {
      console.error('[SurveyHome] Failed to copy link:', error);
      notifyError("Failed to copy link to clipboard.");
    }
  };

  const handleViewSurvey = (surveyId: number) => {
    const survey = surveys.find((s) => s.SurveyId === surveyId);
    if (survey?.PublicToken) {
      navigate(`${ROUTE_SURVEY}?token=${survey.PublicToken}`);
    } else {
      navigate(`${ROUTE_SURVEY}?surveyId=${surveyId}`);
    }
  };

  const handlePreviewSurvey = (surveyId: number) => {
    const survey = surveys.find((s) => s.SurveyId === surveyId);
    if (survey?.PublicToken) {
      navigate(`${ROUTE_PREVIEW_SURVEY}?token=${survey.PublicToken}`);
    } else {
      navigate(`${ROUTE_PREVIEW_SURVEY}?surveyId=${surveyId}`);
    }
  };

  const handleEditSurvey = (surveyId: number) => {
    const survey = surveys.find((s) => s.SurveyId === surveyId);
    if (survey?.PublicToken) {
      navigate(`${ROUTE_CREATE_SURVEY}?token=${survey.PublicToken}`);
    } else {
      navigate(`${ROUTE_CREATE_SURVEY}?surveyId=${surveyId}`);
    }
  };

  const handleSendViaCANmail = (surveyId: number) => {
    navigate(`${ROUTE_EMAIL_SENDER}?surveyId=${surveyId}`);
  };

  const handleDownloadResults = async (surveyId: number) => {
    setDownloadingSurveyId(surveyId);
    try {
      await downloadSurveyResultsExcel(surveyId);
      notifySuccess("Survey results downloaded successfully!");
    } catch (error) {
      console.error('[SurveyHome] Failed to download results:', error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to download survey results.";
      notifyError(errorMessage);
    } finally {
      setDownloadingSurveyId(null);
    }
  };

  const handleDeleteClick = (surveyId: number) => {
    setSelectedSurveyId(surveyId);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedSurveyId(null);
  };

  const handleConfirmDelete = async () => {
    if (selectedSurveyId !== null) {
      setDeletingSurveyId(selectedSurveyId);
      try {
        await deleteSurveyMutation(selectedSurveyId);
        notifySuccess("Survey successfully deleted");
        await refetch();
      } catch (error) {
        console.error('[SurveyHome] Failed to delete survey:', error);
        const errorMessage =
          error instanceof Error ? error.message : "Failed to delete survey";
        notifyError(errorMessage);
      } finally {
        handleDialogClose();
        setDeletingSurveyId(null);
      }
    }
  };

  const handleDeleteResultClick = (surveyResultId: number) => {
    setSelectedResultId(surveyResultId);
    setDeleteResultDialogOpen(true);
  };

  const handleDeleteResultDialogClose = () => {
    setDeleteResultDialogOpen(false);
    setSelectedResultId(null);
  };

  const handleConfirmDeleteResult = async () => {
    if (selectedResultId !== null) {
      setDeletingResult(true);
      try {
        await deleteSurveyResult(selectedResultId);
        notifySuccess("Response deleted successfully");
        setResultsRefreshKey((prev) => prev + 1);
        await refetch();
      } catch (error) {
        console.error('[SurveyHome] Failed to delete result:', error);
        const errorMessage =
          error instanceof Error ? error.message : "Failed to delete response";
        notifyError(errorMessage);
      } finally {
        handleDeleteResultDialogClose();
        setDeletingResult(false);
      }
    }
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value - 1); // MUI Pagination is 1-indexed, API is 0-indexed
  };

  return (
    <Box sx={{ padding: 3 }}>
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 1,
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          My Surveys
        </Typography>
        <Button variant="contained" color="primary" onClick={handleCreateSurvey}>
          Create Survey
        </Button>
      </Box>

      {/* Loading Spinner */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          {error ? (
            <Alert severity="error" sx={{ justifyContent: "center" }}>
              {error}
            </Alert>
          ) : surveys.length === 0 ? (
            <Paper sx={{ py: 4, textAlign: "center" }}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                You haven't created any surveys yet.
              </Typography>
              <Button variant="contained" color="primary" onClick={handleCreateSurvey}>
                Create Survey
              </Button>
            </Paper>
          ) : isMobile ? (
            /* Mobile: card-based survey list */
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {surveys.map((survey) => (
                <SurveyTableRow
                  key={survey.SurveyId}
                  survey={survey}
                  responseCount={responses[survey.SurveyId]}
                  onCopyLink={handleCopyLink}
                  onView={handleViewSurvey}
                  onPreview={handlePreviewSurvey}
                  onEdit={handleEditSurvey}
                  onDelete={handleDeleteClick}
                  onDownload={handleDownloadResults}
                  onSendViaCANmail={handleSendViaCANmail}
                  onManageEditors={(id) => setEditorsDialogSurveyId(id)}
                  onNotifications={(id) => setNotificationsDialogSurveyId(id)}
                  onDeleteResult={handleDeleteResultClick}
                  onAiSummary={(id) => setAiSummarySurveyId(id)}
                  isMobile={true}
                  isDeleting={isDeleting && deletingSurveyId === survey.SurveyId}
                  isDownloading={downloadingSurveyId === survey.SurveyId}
                  isExpanded={expandedSurveyId === survey.SurveyId}
                  onToggle={handleToggleExpand}
                  resultsRefreshKey={resultsRefreshKey}
                />
              ))}
            </Box>
          ) : (
            /* Desktop: table-based survey list */
            <TableContainer component={Paper}>
              <Table sx={{ tableLayout: "fixed" }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 50 }} />
                    <TableCell>
                      <strong>Title</strong>
                    </TableCell>
                    <TableCell sx={{ width: 180 }}>
                      <strong>Created Date</strong>
                    </TableCell>
                    <TableCell align="center" sx={{ width: 90, px: 1 }}>
                      <strong>Responses</strong>
                    </TableCell>
                    <TableCell align="center" sx={{ width: 110, px: 1, whiteSpace: "nowrap" }}>
                      <strong>Copy Link</strong>
                    </TableCell>
                    <TableCell align="center" sx={{ width: 140, px: 1, whiteSpace: "nowrap" }}>
                      <strong>AI Summary</strong>
                    </TableCell>
                    <TableCell align="center" sx={{ width: 100, px: 1, whiteSpace: "nowrap" }}>
                      <strong>Edit</strong>
                    </TableCell>
                    <TableCell align="center" sx={{ width: 80, px: 1, whiteSpace: "nowrap" }}>
                      <strong>Actions</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {surveys.map((survey) => (
                    <SurveyTableRow
                      key={survey.SurveyId}
                      survey={survey}
                      responseCount={responses[survey.SurveyId]}
                      onCopyLink={handleCopyLink}
                      onView={handleViewSurvey}
                      onPreview={handlePreviewSurvey}
                      onEdit={handleEditSurvey}
                      onDelete={handleDeleteClick}
                      onDownload={handleDownloadResults}
                      onSendViaCANmail={handleSendViaCANmail}
                      onManageEditors={(id) => setEditorsDialogSurveyId(id)}
                      onNotifications={(id) => setNotificationsDialogSurveyId(id)}
                      onDeleteResult={handleDeleteResultClick}
                      onAiSummary={(id) => setAiSummarySurveyId(id)}
                      isMobile={false}
                      isDeleting={isDeleting && deletingSurveyId === survey.SurveyId}
                      isDownloading={downloadingSurveyId === survey.SurveyId}
                      isExpanded={expandedSurveyId === survey.SurveyId}
                      onToggle={handleToggleExpand}
                      resultsRefreshKey={resultsRefreshKey}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Pagination
                count={pagination.totalPages}
                page={page + 1} // MUI Pagination is 1-indexed
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Manage Editors Dialog */}
      {editorsDialogSurveyId !== null && (
        <ManageEditorsDialog
          open={true}
          surveyId={editorsDialogSurveyId}
          onClose={() => setEditorsDialogSurveyId(null)}
          onSaved={() => {}}
        />
      )}

      {/* AI Summary Dialog */}
      {aiSummarySurveyId !== null && (
        <AiSummaryDialog
          open={true}
          surveyId={aiSummarySurveyId}
          surveyTitle={surveys.find((s) => s.SurveyId === aiSummarySurveyId)?.SurveyTitle || "Survey"}
          onClose={() => setAiSummarySurveyId(null)}
        />
      )}

      {/* Notifications Dialog */}
      {notificationsDialogSurveyId !== null && (
        <NotificationsDialog
          open={true}
          surveyId={notificationsDialogSurveyId}
          onClose={() => setNotificationsDialogSurveyId(null)}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this survey? All responses and editor access will be permanently removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDialogClose}
            color="primary"
            variant="outlined"
            startIcon={<ArrowBackIosNewIcon />}
            disabled={isDeleting}
          >
            Return
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            startIcon={<DeleteForeverIcon />}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Result Confirmation Dialog */}
      <Dialog open={deleteResultDialogOpen} onClose={handleDeleteResultDialogClose}>
        <DialogTitle>Delete Response</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this survey response? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteResultDialogClose}
            color="primary"
            variant="outlined"
            disabled={deletingResult}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteResult}
            color="error"
            variant="contained"
            startIcon={<DeleteOutlineIcon />}
            disabled={deletingResult}
          >
            {deletingResult ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default SurveyHome;
